import { useEffect, useId, useMemo } from 'react'
import {
  IconPaths,
  initialQueryState,
  ModalSize,
  OptionsHelper,
  useDebounce,
} from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  ActiveFilter,
  ActiveFiltersBar,
  Button,
  CustomSelect,
  IOption,
  Input,
  TableFilter,
  TableFilters,
  useModal,
} from 'app/components'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import {
  DispositivosFilters,
  consultaDosFiltros,
  useListFilters,
} from '../../core/ListFiltersProvider'
import { opcoesRegime, opcoesSituacao, opcoesTipo } from '../../../core/rotulos'
import { buscaOpcoesFornecedores, buscaOpcoesMaletas } from '../../../core/opcoes'
import { DispositivoModal } from '../../../components/modal/DispositivoModal'

const buscaFornecedores = buscaOpcoesFornecedores(false)

export const DispositivosListHeader = () => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { showModal } = useModal()
  const { t } = useT()
  const ids = {
    texto: useId(),
    empresa: useId(),
    tipo: useId(),
    situacao: useId(),
    regime: useId(),
    fornecedor: useId(),
    maleta: useId(),
  }
  const total = response?.totalElements ?? 0
  const debounced = useDebounce(filters, 500)
  const buscaMaletas = useMemo(() => buscaOpcoesMaletas(t), [t])
  const opcoes = useMemo(
    () => ({ regime: opcoesRegime(t), tipo: opcoesTipo(t), situacao: opcoesSituacao(t) }),
    [t]
  )

  const consulta = useMemo(() => consultaDosFiltros(debounced), [debounced])

  useEffect(() => {
    updateState({ ...initialQueryState, ...consulta })
  }, [consulta, updateState])

  const chip = <K extends keyof DispositivosFilters>(
    chave: K,
    rotulo: string,
    valor: string | undefined,
    limpo: DispositivosFilters[K]
  ): ActiveFilter | false =>
    !!valor && {
      key: String(chave),
      label: rotulo,
      value: valor,
      onRemove: () => setFilter(chave, limpo),
    }

  const activeFilters: ActiveFilter[] = [
    chip('texto', t('pagina.dispositivos.filtro.texto'), filters.texto, ''),
    chip('empresa', t('pagina.dispositivos.filtro.empresa'), filters.empresa?.label, undefined),
    chip('tipo', t('pagina.dispositivos.filtro.tipo'), filters.tipo?.label, undefined),
    chip('situacao', t('pagina.dispositivos.filtro.situacao'), filters.situacao?.label, undefined),
    chip('regime', t('pagina.dispositivos.filtro.regime'), filters.regime?.label, undefined),
    chip(
      'fornecedor',
      t('pagina.dispositivos.filtro.fornecedor'),
      filters.fornecedor?.label,
      undefined
    ),
    chip('maleta', t('pagina.dispositivos.filtro.maleta'), filters.maleta?.label, undefined),
  ].filter((filtro): filtro is ActiveFilter => Boolean(filtro))

  const secundarios = [filters.regime, filters.fornecedor, filters.maleta].filter(Boolean).length

  const abrirNovo = () =>
    showModal({
      title: t('pagina.dispositivos.modal.titulo_novo'),
      content: <DispositivoModal />,
      size: ModalSize.ExtraLarge,
    })

  return (
    <div className='card-header border-0 pt-6'>
      <TableFilters
        layout='stacked'
        actions={
          <Button className='btn-primary' icon={IconPaths.Add} onClick={abrirNovo}>
            {t('pagina.dispositivos.novo')}
          </Button>
        }
        moreFiltersActiveCount={secundarios}
        moreFilters={
          <>
            <TableFilter label={t('pagina.dispositivos.filtro.regime')} htmlFor={ids.regime}>
              <CustomSelect
                inputId={ids.regime}
                options={opcoes.regime}
                value={filters.regime}
                onChange={(v?: DispositivosFilters['regime']) =>
                  setFilter('regime', v ?? undefined)
                }
                isClearable
              />
            </TableFilter>
            <TableFilter
              label={t('pagina.dispositivos.filtro.fornecedor')}
              htmlFor={ids.fornecedor}
            >
              <CustomSelect
                inputId={ids.fornecedor}
                fetchOptions={buscaFornecedores}
                filtraNoServidor
                value={filters.fornecedor}
                onChange={(v?: IOption<number>) => setFilter('fornecedor', v ?? undefined)}
                isClearable
              />
            </TableFilter>
            <TableFilter label={t('pagina.dispositivos.filtro.maleta')} htmlFor={ids.maleta}>
              <CustomSelect
                inputId={ids.maleta}
                fetchOptions={buscaMaletas}
                filtraNoServidor
                value={filters.maleta}
                onChange={(v?: IOption<number>) => setFilter('maleta', v ?? undefined)}
                isClearable
              />
            </TableFilter>
          </>
        }
        footer={
          <ActiveFiltersBar
            filters={activeFilters}
            onClearAll={clearFilters}
            summary={
              isLoading
                ? t('pagina.dispositivos.buscando')
                : t('pagina.dispositivos.total', { total })
            }
          />
        }
      >
        <TableFilter
          label={t('pagina.dispositivos.filtro.texto')}
          htmlFor={ids.texto}
          hint={t('pagina.dispositivos.filtro.texto_ajuda')}
          wide
        >
          <Input
            id={ids.texto}
            value={filters.texto}
            onChange={(e) => setFilter('texto', e.target.value)}
          />
        </TableFilter>
        <TableFilter label={t('pagina.dispositivos.filtro.empresa')} htmlFor={ids.empresa}>
          <CustomSelect
            inputId={ids.empresa}
            fetchOptions={OptionsHelper.getOptionsEmpresas}
            fetchOptionsParams={{ onlyActive: false }}
            value={filters.empresa}
            onChange={(v?: IOption<number>) => setFilter('empresa', v ?? undefined)}
            isClearable
          />
        </TableFilter>
        <TableFilter label={t('pagina.dispositivos.filtro.tipo')} htmlFor={ids.tipo}>
          <CustomSelect
            inputId={ids.tipo}
            options={opcoes.tipo}
            value={filters.tipo}
            onChange={(v?: DispositivosFilters['tipo']) => setFilter('tipo', v ?? undefined)}
            isClearable
          />
        </TableFilter>
        <TableFilter label={t('pagina.dispositivos.filtro.situacao')} htmlFor={ids.situacao}>
          <CustomSelect
            inputId={ids.situacao}
            options={opcoes.situacao}
            value={filters.situacao}
            onChange={(v?: DispositivosFilters['situacao']) =>
              setFilter('situacao', v ?? undefined)
            }
            isClearable
          />
        </TableFilter>
      </TableFilters>
    </div>
  )
}
